import React, { useState, useEffect, useCallback } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  ClockIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  Cog6ToothIcon,
  IdentificationIcon,
  LockClosedIcon,
  EyeIcon,
  CheckCircleIcon,
  AtSymbolIcon,
  PlusIcon,
  TrashIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import { StatusBadge } from '../ui/Table';
import PermissionManager from './PermissionManager';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

// Password Reset Dialog Component
const PasswordResetDialog = ({ open, loading, onClose, onReset, userEmail }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requireChange, setRequireChange] = useState(true);
  const [generatePassword, setGeneratePassword] = useState(false);
  const [error, setError] = useState('');

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    setGeneratePassword(true);
  };

  const handleReset = () => {
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    onReset(password, requireChange);
  };

  const handleClose = () => {
    setPassword('');
    setConfirmPassword('');
    setRequireChange(true);
    setGeneratePassword(false);
    setError('');
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl p-6 w-full max-w-md mx-4 border border-gray-200 dark:border-gray-800">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Reset Password
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Reset password for: <strong>{userEmail}</strong>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Password
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="px-3 py-2 bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-[#2a2a2a] text-sm"
              >
                Generate
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password
            </label>
            <input
              type="text"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Confirm new password"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireChange"
              checked={requireChange}
              onChange={(e) => setRequireChange(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="requireChange" className="text-sm text-gray-700 dark:text-gray-300">
              Require password change on next login
            </label>
          </div>

          {generatePassword && password && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-md text-sm">
              <strong>Generated Password:</strong> {password}
              <br />
              <small>Please save this password as it won't be shown again.</small>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditUserSidePanel = ({
  isOpen,
  onClose,
  user,
  onSave,
  onUserUpdate,
  availableDomains = [],
  loading = false,
  size = '3xl',
  showNotification
}) => {
  const [activeTab, setActiveTab] = useState('account');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    roleId: null,
    status: '',
    department: '',
    jobTitle: '',
    phoneNumber: '',
    blockSignIn: false,
    blockReason: '',
    requirePasswordChange: false,
    passwordExpiry: false,
    mailboxQuota: '25GB',
    forwardingEnabled: false,
    forwardingAddress: '',
    forwardingAddressInput: '',
    forwardingPermissions: [],
    delegateAccess: [],
    retentionPolicy: 'default',
    litigationHold: false,
    mfaEnabled: false,
    mailboxPermissions: {
      fullAccess: [],
      sendAs: [],
      sendOnBehalf: []
    },
    calendarPermissions: {
      read: [],
      write: [],
      delegate: []
    },
    autoReply: {
      enabled: false,
      message: '',
      startDate: '',
      endDate: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [permissionDialog, setPermissionDialog] = useState({
    open: false,
    type: null,
    title: '',
  });
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [passwordResetDialog, setPasswordResetDialog] = useState({
    open: false,
    loading: false
  });
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  
  // Email alias management state
  const [emailAliases, setEmailAliases] = useState([]);
  const [loadingAliases, setLoadingAliases] = useState(false);
  const [newAliasEmail, setNewAliasEmail] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [verifiedDomains, setVerifiedDomains] = useState([]);
  const [addingAlias, setAddingAlias] = useState(false);
  const [emailInputMode, setEmailInputMode] = useState('full'); // 'full' or 'username-domain'
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  const tabs = [
    { id: 'account', label: 'Account', icon: IdentificationIcon },
    { id: 'usernames', label: 'Usernames', icon: AtSymbolIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'mailbox', label: 'Mailbox', icon: InboxIcon },
    { id: 'settings', label: 'Settings', icon: Cog6ToothIcon }
  ];

  // Show success indicator when user data is updated
  useEffect(() => {
    if (user && showUpdateSuccess) {
      const timer = setTimeout(() => {
        setShowUpdateSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user, showUpdateSuccess]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Use onSave or onUserUpdate (for backward compatibility)
      const submitFunction = onSave || onUserUpdate;
      if (typeof submitFunction === 'function') {
        try {
          await submitFunction(formData);
          // Show success indicator
          setShowUpdateSuccess(true);
        } catch (error) {
          logger.error('Error in handleSubmit:', error);
        }
      } else {
        logger.error('No valid submit function provided (onSave or onUserUpdate)');
      }
    }
  };

  const formatLastLogin = (date) => {
    if (!date) return 'Never';
    const loginDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - loginDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return loginDate.toLocaleDateString();
  };

  const isEmailDomainVerified = () => {
    if (!formData.email || !availableDomains.length) {
      return false;
    }

    const domain = formData.email.split('@')[1];
    if (!domain) {
      return false;
    }

    // Business owner exception - domain treated as verified
    if (formData.email === 'afzaal@aiinak.com') {
      return true;
    }

    const matchingDomain = availableDomains.find(d => d.domainName === domain);
    return matchingDomain && matchingDomain.isVerified === true;
  };

  // Fetch mailbox permissions
  const fetchMailboxPermissions = useCallback(async () => {
    try {
      setLoadingPermissions(true);
      const userId = user.id || user._id;
      const response = await axiosInstance.get(`/api/business/users/${userId}/permissions`);
      
      if (response.data.success) {
        const { permissions } = response.data;
        const groupedPermissions = {
          fullAccess: [],
          sendAs: [],
          sendOnBehalf: []
        };
        
        // Handle mailbox permissions from the database schema
        if (permissions.mailboxPermissions) {
          Object.keys(permissions.mailboxPermissions).forEach(permType => {
            const permList = permissions.mailboxPermissions[permType] || [];

            const seen = new Set();
            groupedPermissions[permType] = permList.map(perm => ({
              id: perm.userId._id || perm.userId,
              firstName: perm.userId.firstName || '',
              lastName: perm.userId.lastName || '',
              email: perm.userId.email || ''
            })).filter(u => {
              if (seen.has(u.id)) return false;
              seen.add(u.id);
              return true;
            });
          });
        }
        
        setFormData(prev => ({
          ...prev,
          mailboxPermissions: groupedPermissions,
          calendarPermissions: permissions.calendarPermissions || {
            read: [],
            write: [],
            delegate: []
          },
          forwardingEnabled: permissions.forwardingEnabled || false,
          forwardingAddress: permissions.forwardingAddress || '',
          autoReply: permissions.autoReply || {
            enabled: false,
            message: '',
            startDate: '',
            endDate: ''
          }
        }));
      }
    } catch (error) {
      logger.error('Error fetching mailbox permissions:', error);
      setFormData(prev => ({
        ...prev,
        mailboxPermissions: {
          fullAccess: [],
          sendAs: [],
          sendOnBehalf: []
        },
        calendarPermissions: {
          read: [],
          write: [],
          delegate: []
        }
      }));
    } finally {
      setLoadingPermissions(false);
    }
  }, [user]);

  // Fetch forwarding permissions
  const fetchForwardingPermissions = useCallback(async () => {
    try {
      const userId = user.id || user._id;
      const response = await axiosInstance.get(`/api/business/users/forwarding-permissions/${userId}`);
      
      if (response.data.success) {
        let forwardingUsers = [];
        
        if (response.data.forwardingEnabled && response.data.forwardingAddress) {
          forwardingUsers = [{
            id: 'forwarding-' + Date.now(),
            email: response.data.forwardingAddress,
            firstName: response.data.forwardingAddress.split('@')[0],
            lastName: ''
          }];
        }

        setFormData(prev => ({
          ...prev,
          forwardingPermissions: forwardingUsers,
          forwardingEnabled: response.data.forwardingEnabled || false,
          forwardingAddress: response.data.forwardingAddress || '',
          forwardingAddressInput: ''
        }));
      }
    } catch (error) {
      logger.error('Error fetching forwarding permissions:', error);
    }
  }, [user]);

  // Fetch available roles
  const fetchAvailableRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await axiosInstance.get('/api/business/roles');
      
      if (response.data.success) {
        const roles = response.data.roles || [];
        setAvailableRoles(roles);
      } else {
        setAvailableRoles([]);
      }
    } catch (error) {
      logger.error('Error fetching available roles:', error);
      setAvailableRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  // Email alias management functions
  const fetchEmailAliases = useCallback(async () => {
    try {
      setLoadingAliases(true);
      const userId = user.id || user._id;
      const response = await axiosInstance.get(`/api/business/users/${userId}/email-aliases`);

      if (response.data.success) {
        setEmailAliases(response.data.aliases || []);
        setVerifiedDomains(response.data.verifiedDomains || []);

        // Set first verified domain as default if available
        if (response.data.verifiedDomains && response.data.verifiedDomains.length > 0) {
          setSelectedDomain(response.data.verifiedDomains[0].id);
        }
      }
    } catch (error) {
      logger.error('Error fetching email aliases:', error);
      setEmailAliases([]);
    } finally {
      setLoadingAliases(false);
    }
  }, [user]);

  // Initialize form data and fetch permissions when modal opens
  useEffect(() => {
    if (user && isOpen) {
      setActiveTab('account');
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || '',
        roleId: user.roleId || user.roleInfo?.id || null,
        status: user.status || '',
        department: user.department || '',
        jobTitle: user.jobTitle || '',
        phoneNumber: user.phoneNumber || '',
        blockSignIn: user.blockSignIn || false,
        blockReason: user.blockReason || '',
        requirePasswordChange: user.requirePasswordChange || false,
        passwordExpiry: user.passwordExpiry || false,
        mailboxQuota: user.mailboxQuota || '25GB',
        forwardingEnabled: user.forwardingEnabled || false,
        forwardingAddress: user.forwardingAddress || '',
        forwardingAddressInput: '',
        forwardingPermissions: [],
        delegateAccess: user.delegateAccess || [],
        retentionPolicy: user.retentionPolicy || 'default',
        litigationHold: user.litigationHold || false,
        mfaEnabled: user.mfaEnabled || false,
        mailboxPermissions: {
          fullAccess: [],
          sendAs: [],
          sendOnBehalf: []
        },
        calendarPermissions: {
          read: [],
          write: [],
          delegate: []
        },
        autoReply: {
          enabled: user.autoReply?.enabled || false,
          message: user.autoReply?.message || '',
          startDate: user.autoReply?.startDate || '',
          endDate: user.autoReply?.endDate || ''
        }
      });
      setErrors({});

      // Fetch permissions and roles when modal opens
      fetchMailboxPermissions();
      fetchForwardingPermissions();
      fetchAvailableRoles();
      fetchEmailAliases();
    }
  }, [user, isOpen, fetchMailboxPermissions, fetchForwardingPermissions, fetchEmailAliases]);

  const handleAddEmailAlias = async () => {
    if (!newAliasEmail.trim()) return;

    try {
      setAddingAlias(true);
      const userId = user.id || user._id;
      
      let emailToAdd = newAliasEmail.trim();
      let domainId = null;
      
      // If in username-domain mode, construct full email
      if (emailInputMode === 'username-domain' && selectedDomain) {
        const selectedDomainObj = verifiedDomains.find(d => d.id === selectedDomain);
        if (selectedDomainObj) {
          emailToAdd = `${newAliasEmail.trim()}@${selectedDomainObj.name}`;
          domainId = selectedDomain;
        }
      }
      
      const response = await axiosInstance.post(`/api/business/users/${userId}/email-aliases`, {
        email: emailToAdd,
        domainId: domainId
      });
      
      if (response.data.success) {
        await fetchEmailAliases(); // Refresh the list
        setNewAliasEmail('');
      }
    } catch (error) {
      logger.error('Error adding email alias:', error);
      showNotification?.(error.response?.data?.message || 'Failed to add email alias', 'error');
    } finally {
      setAddingAlias(false);
    }
  };

  const handleRemoveEmailAlias = async (aliasId) => {
    try {
      const userId = user.id || user._id;
      const response = await axiosInstance.delete(`/api/business/users/${userId}/email-aliases/${aliasId}`);
      
      if (response.data.success) {
        await fetchEmailAliases(); // Refresh the list
      }
    } catch (error) {
      logger.error('Error removing email alias:', error);
      showNotification?.(error.response?.data?.message || 'Failed to remove email alias', 'error');
    }
  };

  const handleSetPrimaryAlias = async (aliasId) => {
    try {
      const userId = user.id || user._id;
      const response = await axiosInstance.put(`/api/business/users/${userId}/email-aliases/${aliasId}/set-primary`);
      
      if (response.data.success) {
        await fetchEmailAliases(); // Refresh the list
      }
    } catch (error) {
      logger.error('Error setting primary alias:', error);
      showNotification?.(error.response?.data?.message || 'Failed to set primary alias', 'error');
    }
  };

  // Permission dialog handlers
  const handlePermissionDialogOpen = (type, title) => {
    setPermissionDialog({
      open: true,
      type,
      title,
    });
  };

  const handlePermissionDialogClose = () => {
    setPermissionDialog({
      open: false,
      type: null,
      title: '',
    });
  };

  const handlePasswordReset = async (newPassword, requireChange = true) => {
    try {
      setPasswordResetDialog(prev => ({ ...prev, loading: true }));
      
      const userId = user?.id || user?._id;
      const response = await axiosInstance.post(`/api/business/users/${userId}/reset-password`, {
        password: newPassword,
        requireChange
      });

      if (response.data.success) {
        setShowUpdateSuccess(true);
        setPasswordResetDialog({ open: false, loading: false });
        
        // Update form data to reflect password change requirement
        setFormData(prev => ({
          ...prev,
          requirePasswordChange: requireChange
        }));
        
        setTimeout(() => setShowUpdateSuccess(false), 3000);
      }
    } catch (error) {
      logger.error('Error resetting password:', error);
      setPasswordResetDialog(prev => ({ ...prev, loading: false }));
      // You might want to show an error toast here
    }
  };

  const handleAddEmailForwarding = async () => {
    const emailToAdd = formData.forwardingAddressInput?.trim();
    
    if (!emailToAdd) {
      showNotification?.('Please enter an email address', 'error');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToAdd)) {
      showNotification?.('Please enter a valid email address', 'error');
      return;
    }
    
    try {
      const userId = user?.id || user?._id;

      // Call backend to enable forwarding
      const response = await axiosInstance.post(`/api/business/users/${userId}/email-forwarding`, {
        enabled: true,
        forwardingAddress: emailToAdd
      });

      if (response.data.success) {
        // Update form state - optimistic UI update
        setFormData(prev => ({
          ...prev,
          forwardingEnabled: true,
          forwardingAddress: emailToAdd,
          forwardingAddressInput: '' // Clear input
        }));

        setShowUpdateSuccess(true);
        setTimeout(() => setShowUpdateSuccess(false), 3000);
      }
    } catch (error) {
      logger.error('Error adding email forwarding:', error);
      showNotification?.(error.response?.data?.message || 'Failed to add email forwarding', 'error');
    }
  };

  const handleRemoveEmailForwarding = async () => {
    if (!window.confirm('Are you sure you want to remove email forwarding?')) {
      return;
    }

    try {
      const userId = user?.id || user?._id;

      // Call backend to disable forwarding
      const response = await axiosInstance.delete(`/api/business/users/email-forwarding/${userId}`);

      if (response.data.success) {
        // Update form state
        setFormData(prev => ({
          ...prev,
          forwardingEnabled: false,
          forwardingAddress: '',
          forwardingAddressInput: ''
        }));

        setShowUpdateSuccess(true);
        setTimeout(() => setShowUpdateSuccess(false), 3000);
      }
    } catch (error) {
      logger.error('Error removing email forwarding:', error);
      showNotification?.(error.response?.data?.message || 'Failed to remove email forwarding', 'error');
    }
  };

  const handleDeleteUser = async () => {
    try {
      setDeletingUser(true);
      const userId = user?.id || user?._id;

      const response = await axiosInstance.delete(`/api/business/users/${userId}`);

      if (response.data.success) {
        setShowDeleteConfirmation(false);
        onClose();

        // Notify parent component to refresh user list
        if (onUserUpdate) {
          onUserUpdate();
        }
      }
    } catch (error) {
      logger.error('Error deleting user:', error);
      showNotification?.(error.response?.data?.message || 'Failed to delete user', 'error');
    } finally {
      setDeletingUser(false);
    }
  };

  const handlePermissionSave = async (users) => {
    const permissionType = permissionDialog.type;
    
    // Update local form state for immediate UI feedback
    if (permissionType.startsWith('mailbox.')) {
      const permissionKey = permissionType.split('.')[1];
      setFormData(prev => ({
        ...prev,
        mailboxPermissions: {
          ...prev.mailboxPermissions,
          [permissionKey]: users,
        }
      }));
    } else if (permissionType.startsWith('calendar.')) {
      const permissionKey = permissionType.split('.')[1];
      setFormData(prev => ({
        ...prev,
        calendarPermissions: {
          ...prev.calendarPermissions,
          [permissionKey]: users,
        }
      }));
    } else if (permissionType === 'forwarding') {
      setFormData(prev => ({
        ...prev,
        forwardingPermissions: users,
      }));
    }

    // Refresh permissions from server
    try {
      if (permissionType.startsWith('mailbox.')) {
        await fetchMailboxPermissions();
      } else if (permissionType === 'forwarding') {
        await fetchForwardingPermissions();
      }
    } catch (error) {
      logger.error('Error refreshing permissions:', error);
    }

    handlePermissionDialogClose();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return renderAccountTab();
      case 'usernames':
        return renderUsernamesTab();
      case 'security':
        return renderSecurityTab();
      case 'mailbox':
        return renderMailboxTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderAccountTab();
    }
  };

  const renderAccountTab = () => (
    <div className="space-y-6 bg-white dark:bg-[#1a1a1a]">
      {/* Personal Information */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <UserIcon className="w-4 h-4 mr-2 text-orange-500" />
          Personal Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                errors.firstName ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-700'
              }`}
              placeholder="Enter first name"
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>
            )}
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                errors.lastName ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-700'
              }`}
              placeholder="Enter last name"
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>
            )}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <EnvelopeIcon className="w-4 h-4 mr-2 text-orange-500" />
          Contact Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                errors.email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-700'
              }`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email}</p>
            )}
            {/* Domain verification status - only show if not verified */}
            {formData.email && formData.email.includes('@') && !isEmailDomainVerified() && (
              <div className="mt-1 flex items-center text-amber-600 text-xs">
                <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                <span>Domain not verified</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>

      {/* Work Information */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <BuildingOfficeIcon className="w-4 h-4 mr-2 text-orange-500" />
          Work Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Department
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter department"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Job Title
            </label>
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter job title"
            />
          </div>
        </div>
      </div>

      {/* Role & Status */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <IdentificationIcon className="w-4 h-4 mr-2 text-orange-500" />
          Role & Status
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role *
            </label>
            <select
              value={formData.roleId || ''}
              onChange={(e) => handleInputChange('roleId', e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                errors.role ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-700'
              }`}
              disabled={loadingRoles}
            >
              <option value="">Select a role...</option>
              {availableRoles.map(role => (
                <option key={role._id} value={role._id}>
                  {role.displayName} (Level {role.level})
                </option>
              ))}
            </select>
            {loadingRoles && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading roles...</p>
            )}
            {formData.roleId && (
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                {availableRoles.find(r => r._id === formData.roleId)?.description}
              </p>
            )}
            {errors.role && (
              <p className="mt-1 text-xs text-red-600">{errors.role}</p>
            )}
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status *
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                errors.status ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-700'
              }`}
            >
              <option value="">Select status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-xs text-red-600">{errors.status}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsernamesTab = () => (
    <div className="space-y-6 bg-white dark:bg-[#1a1a1a]">
      {/* Primary Email */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <AtSymbolIcon className="w-4 h-4 mr-2 text-orange-500" />
          Primary Email Address
        </h4>
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <StarIcon className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{user?.email}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                  Primary
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">This is the main login email address</p>
            </div>
          </div>
        </div>
      </div>

      {/* Email Aliases */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <EnvelopeIcon className="w-4 h-4 mr-2 text-orange-500" />
          Additional Email Addresses ({emailAliases.length})
        </h4>
        
        {/* Add New Alias */}
        <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-medium text-gray-900 dark:text-white">Add New Email Address</h5>
            {verifiedDomains.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-600">Mode:</span>
                <select
                  value={emailInputMode}
                  onChange={(e) => {
                    setEmailInputMode(e.target.value);
                    setNewAliasEmail(''); // Clear input when switching modes
                  }}
                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="full">Full Email</option>
                  <option value="username-domain">Username + Domain</option>
                </select>
              </div>
            )}
          </div>
          
          {emailInputMode === 'full' ? (
            <div className="flex gap-2">
              <input
                type="email"
                value={newAliasEmail}
                onChange={(e) => setNewAliasEmail(e.target.value)}
                placeholder="Enter full email address..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !addingAlias) {
                    handleAddEmailAlias();
                  }
                }}
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleAddEmailAlias}
                disabled={!newAliasEmail.trim() || addingAlias}
                loading={addingAlias}
                icon={<PlusIcon />}
              >
                Add
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Domain Selection */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Select Verified Domain
                </label>
                <select
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select a domain...</option>
                  {verifiedDomains.map(domain => (
                    <option key={domain.id} value={domain.id}>
                      {domain.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Username Input */}
              <div className="flex gap-2">
                <div className="flex-1 flex">
                  <input
                    type="text"
                    value={newAliasEmail}
                    onChange={(e) => setNewAliasEmail(e.target.value)}
                    placeholder="username"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-md focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !addingAlias && selectedDomain) {
                        handleAddEmailAlias();
                      }
                    }}
                  />
                  <div className="px-3 py-2 text-sm bg-gray-100 border border-l-0 border-gray-300 rounded-r-md flex items-center">
                    @{verifiedDomains.find(d => d.id === selectedDomain)?.name || 'domain.com'}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleAddEmailAlias}
                  disabled={!newAliasEmail.trim() || !selectedDomain || addingAlias}
                  loading={addingAlias}
                  icon={<PlusIcon />}
                >
                  Add
                </Button>
              </div>
            </div>
          )}
          
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
            <p className="text-xs text-blue-800 dark:text-blue-200 mb-1">
              <strong>Verified Domains ({verifiedDomains.length}):</strong>
            </p>
            {verifiedDomains.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {verifiedDomains.map(domain => (
                  <span key={domain.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {domain.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-blue-600 dark:text-blue-400">No verified domains available. Contact your administrator to verify domains.</p>
            )}
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Users can login with any of these email addresses using the same password
          </p>
        </div>

        {/* Aliases List */}
        {loadingAliases ? (
          <div className="flex items-center justify-center p-8">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-500 rounded-full animate-pulse"></div>
              <span className="text-gray-600 dark:text-gray-400 text-sm">Loading email addresses...</span>
            </div>
          </div>
        ) : emailAliases.length > 0 ? (
          <div className="space-y-2">
            {emailAliases.map((alias) => (
              <div key={alias.id} className="bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{alias.email}</span>
                      {alias.isPrimary && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Active Primary
                        </span>
                      )}
                      {alias.domainName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {alias.domainName}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Created {new Date(alias.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!alias.isPrimary && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => handleSetPrimaryAlias(alias.id)}
                        className="text-blue-600 hover:text-blue-700"
                        icon={<StarIcon />}
                      >
                        Set Primary
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="danger"
                      size="xs"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to remove ${alias.email}?`)) {
                          handleRemoveEmailAlias(alias.id);
                        }
                      }}
                      icon={<TrashIcon />}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-8 bg-gray-50 dark:bg-[#222222] rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
            <AtSymbolIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">No additional email addresses</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Add email aliases to allow this user to login with multiple addresses
            </p>
          </div>
        )}
      </div>

      {/* Benefits Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">Benefits of Multiple Email Addresses</h5>
        <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
          <li>• Users can login with any email address using the same password</li>
          <li>• Support for multiple domains (e.g., john@company.com, john@subsidiary.com)</li>
          <li>• Role-based addresses (e.g., ceo@company.com, president@company.com)</li>
          <li>• Professional aliases (e.g., john.smith@company.com, js@company.com)</li>
          <li>• Simplified management with unified authentication</li>
        </ul>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6 bg-white dark:bg-[#1a1a1a]">
      {/* Access Control */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <ShieldCheckIcon className="w-4 h-4 mr-2 text-orange-500" />
          Access Control
        </h4>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Sign-in Access</h5>
                <p className="text-xs text-gray-600 dark:text-gray-400">Control whether user can sign in</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.blockSignIn}
                  onChange={(e) => handleInputChange('blockSignIn', !e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
            
            {formData.blockSignIn && (
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Block Reason
                </label>
                <textarea
                  value={formData.blockReason}
                  onChange={(e) => handleInputChange('blockReason', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter reason for blocking access..."
                />
              </div>
            )}
          </div>
          
          <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Password Reset Required</h5>
                <p className="text-xs text-gray-600 dark:text-gray-400">Force password change on next login</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.requirePasswordChange}
                  onChange={(e) => handleInputChange('requirePasswordChange', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Multi-Factor Authentication</h5>
                <p className="text-xs text-gray-600 dark:text-gray-400">Add an extra layer of security</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.mfaEnabled}
                  onChange={(e) => handleInputChange('mfaEnabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
          </div>

          {/* Password Expiry option temporarily removed */}
        </div>
      </div>

      {/* Password Actions - hidden for platform owner */}
      {!user?.isOwner && (
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <KeyIcon className="w-4 h-4 mr-2 text-orange-500" />
          Password Management
        </h4>
        <div className="space-y-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full justify-center"
            icon={<KeyIcon />}
            onClick={() => setPasswordResetDialog({ open: true, loading: false })}
          >
            Reset Password
          </Button>
        </div>
      </div>
      )}
    </div>
  );

  const renderMailboxTab = () => (
    <div className="space-y-6 bg-white dark:bg-[#1a1a1a]">
      {/* Storage & Quota Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <InboxIcon className="w-4 h-4 mr-2 text-orange-500" />
          Storage & Quota
        </h4>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mailbox Quota</label>
          <select
            value={formData.mailboxQuota}
            onChange={(e) => handleInputChange('mailboxQuota', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="25GB">25 GB</option>
            <option value="50GB">50 GB</option>
            <option value="100GB">100 GB</option>
            <option value="unlimited">Unlimited</option>
          </select>
        </div>
      </div>

      {/* Email Forwarding Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <EnvelopeIcon className="w-4 h-4 mr-2 text-orange-500" />
          Email Forwarding
        </h4>
        <div className="space-y-4">
          {/* Forwarding Email Display */}
          {formData.forwardingEnabled && formData.forwardingAddress ? (
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Forwarding Destination</h5>
              <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                    <EnvelopeIcon className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{formData.forwardingAddress}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">All emails will be forwarded here</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveEmailForwarding()}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Remove forwarding"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Add Email Forwarding</h5>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={formData.forwardingAddressInput || ''}
                  onChange={(e) => handleInputChange('forwardingAddressInput', e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter email address to forward to..."
                />
                <button
                  type="button"
                  onClick={() => handleAddEmailForwarding()}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors whitespace-nowrap"
                >
                  Add Email
                </button>
              </div>
            </div>
          )}
          
          <button
            type="button"
            onClick={() => handlePermissionDialogOpen('forwarding', 'Manage Email Forwarding Permissions')}
            className="w-full flex items-center justify-between p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
          >
            <div className="flex items-center space-x-2">
              <EnvelopeIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Forwarding Permissions</span>
            </div>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              {formData.forwardingPermissions.length}
            </span>
          </button>
        </div>
      </div>

      {/* Mailbox Permissions Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <ShieldCheckIcon className="w-4 h-4 mr-2 text-orange-500" />
          Mailbox Permissions
        </h4>
        {loadingPermissions ? (
          <div className="flex items-center justify-center p-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
              <span className="text-gray-600 dark:text-gray-400 text-sm">Loading permissions...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => handlePermissionDialogOpen('mailbox.fullAccess', 'Manage Full Access Permissions')}
              className="flex flex-col items-center p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mb-1">
                <LockClosedIcon className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Full Access</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {formData.mailboxPermissions.fullAccess.length} users
              </span>
            </button>

            <button
              type="button"
              onClick={() => handlePermissionDialogOpen('mailbox.sendAs', 'Manage Send As Permissions')}
              className="flex flex-col items-center p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
            >
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mb-1">
                <EnvelopeIcon className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Send As</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {formData.mailboxPermissions.sendAs.length} users
              </span>
            </button>

            <button
              type="button"
              onClick={() => handlePermissionDialogOpen('mailbox.sendOnBehalf', 'Manage Send on Behalf Permissions')}
              className="flex flex-col items-center p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
            >
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mb-1">
                <UserIcon className="w-4 h-4 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Send on Behalf</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {formData.mailboxPermissions.sendOnBehalf.length} users
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Calendar Permissions Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <ClockIcon className="w-4 h-4 mr-2 text-orange-500" />
          Calendar Permissions
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => handlePermissionDialogOpen('calendar.read', 'Manage Calendar Read Permissions')}
            className="flex flex-col items-center p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
          >
            <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-1">
              <EyeIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">Read Access</span>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {formData.calendarPermissions.read.length} users
            </span>
          </button>

          <button
            type="button"
            onClick={() => handlePermissionDialogOpen('calendar.write', 'Manage Calendar Write Permissions')}
            className="flex flex-col items-center p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
          >
            <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-1">
              <CheckCircleIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">Write Access</span>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {formData.calendarPermissions.write.length} users
            </span>
          </button>

          <button
            type="button"
            onClick={() => handlePermissionDialogOpen('calendar.delegate', 'Manage Calendar Delegate Permissions')}
            className="flex flex-col items-center p-3 bg-white dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
          >
            <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mb-1">
              <UserIcon className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            </div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">Delegate Access</span>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {formData.calendarPermissions.delegate.length} users
            </span>
          </button>
        </div>
      </div>

      {/* Auto Reply Section */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <Cog6ToothIcon className="w-4 h-4 mr-2 text-orange-500" />
          Auto Reply Settings
        </h4>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Enable Auto Reply</h5>
                <p className="text-xs text-gray-600 dark:text-gray-400">Automatically respond to incoming emails</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.autoReply.enabled}
                  onChange={(e) => handleInputChange('autoReply', {
                    ...formData.autoReply,
                    enabled: e.target.checked
                  })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
            
            {formData.autoReply.enabled && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Auto Reply Message</label>
                  <textarea
                    value={formData.autoReply.message}
                    onChange={(e) => handleInputChange('autoReply', {
                      ...formData.autoReply,
                      message: e.target.value
                    })}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Thank you for your email. I will respond as soon as possible..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                    <input
                      type="datetime-local"
                      value={formData.autoReply.startDate}
                      onChange={(e) => handleInputChange('autoReply', {
                        ...formData.autoReply,
                        startDate: e.target.value
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                    <input
                      type="datetime-local"
                      value={formData.autoReply.endDate}
                      onChange={(e) => handleInputChange('autoReply', {
                        ...formData.autoReply,
                        endDate: e.target.value
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6 bg-white dark:bg-[#1a1a1a]">
      {/* Notification Preferences */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <Cog6ToothIcon className="w-4 h-4 mr-2 text-orange-500" />
          Notification Preferences
        </h4>
        <div className="space-y-3">
          <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</h5>
                <p className="text-xs text-gray-600 dark:text-gray-400">Receive email updates</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">Security Alerts</h5>
                <p className="text-xs text-gray-600 dark:text-gray-400">Get notified of security events</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Account Actions - hidden for platform owner */}
      {!user?.isOwner && (
      <div>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center border-b border-gray-200 dark:border-gray-700 pb-2">
          <ExclamationTriangleIcon className="w-4 h-4 mr-2 text-red-500" />
          Danger Zone
        </h4>
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="text-sm font-medium text-red-900 dark:text-red-200 mb-1">Delete Account</h5>
              <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                This action cannot be undone. All user data, emails, and access will be permanently removed.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteConfirmation(true)}
            className="w-full justify-center text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-300 dark:border-red-700"
          >
            Delete Account
          </Button>
        </div>
      </div>
      )}
    </div>
  );

  return (
    <SidePanel 
      isOpen={isOpen} 
      onClose={onClose} 
      title="" 
      size={size}
    >
      <form onSubmit={handleSubmit} className="h-full flex flex-col bg-white dark:bg-[#1a1a1a]">
        {/* Compact Header Section */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-white">
                {formData.firstName?.charAt(0)}{formData.lastName?.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {formData.firstName} {formData.lastName}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{formData.email}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {showUpdateSuccess && (
                <div className="flex items-center gap-1 text-green-600 text-xs font-medium animate-pulse">
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Updated!</span>
                </div>
              )}
              <StatusBadge
                status={formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}
                variant={formData.status === 'active' ? 'success' : formData.status === 'inactive' ? 'default' : 'error'}
              />
              {user?.lastLoginAt && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatLastLogin(user.lastLoginAt)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111]">
          <nav className="flex space-x-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600 bg-white dark:bg-[#1a1a1a]'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222]'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-[#1a1a1a]">
          {renderTabContent()}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111] px-6 py-3">
          <div className="flex justify-between items-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={loading}
                disabled={loading}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </form>
      
      {/* Permission Management Dialog */}
      {/* Password Reset Dialog */}
      {passwordResetDialog.open && (
        <PasswordResetDialog
          open={passwordResetDialog.open}
          loading={passwordResetDialog.loading}
          onClose={() => setPasswordResetDialog({ open: false, loading: false })}
          onReset={handlePasswordReset}
          userEmail={user?.email}
        />
      )}

      {permissionDialog.open && (
        <PermissionManager
          open={permissionDialog.open}
          onClose={handlePermissionDialogClose}
          title={permissionDialog.title}
          type={permissionDialog.type}
          currentPermissions={(() => {
            let permissions = [];
            if (permissionDialog.type?.startsWith('mailbox.')) {
              const permType = permissionDialog.type.split('.')[1];
              permissions = formData.mailboxPermissions[permType] || [];
            } else if (permissionDialog.type?.startsWith('calendar.')) {
              const permType = permissionDialog.type.split('.')[1];
              permissions = formData.calendarPermissions[permType] || [];
            } else if (permissionDialog.type === 'forwarding') {
              permissions = formData.forwardingPermissions || [];
            }
            return permissions;
          })()
          }
          onSave={handlePermissionSave}
          userId={user?.id || user?._id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10002] p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete User Account</h3>
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-[#222222]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Are you sure you want to delete this user?
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user?.firstName} {user?.lastName} ({user?.email})
                  </p>
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-800 dark:text-red-200">
                  <strong>Warning:</strong> This action cannot be undone. All user data, emails, mailbox content, and access permissions will be permanently removed.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111]">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(false)}
                disabled={deletingUser}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#222222] rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={deletingUser}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors min-w-[120px] justify-center"
              >
                {deletingUser ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default EditUserSidePanel;